import { FIELD } from "@golazo/shared";

/**
 * Geometria pura del encuadre: donde poner la camara y que trozo de campo
 * queda a la vista.
 *
 * Vive aparte de `mobile/viewport.ts` porque aquel importa el HUD (y con el,
 * Pixi) para leer la franja del marcador. Esto es aritmetica y no depende del
 * navegador, asi que separandolo se puede probar en Node — que es justo lo que
 * hace falta: el recorte es la parte que, si se equivoca, enseña el vacio de
 * detras del estadio.
 */
export interface FrameRect {
  x: number;
  y: number;
  width: number;
  height: number;
}

/**
 * Aire alrededor del cesped al encajar en pantalla, en unidades de mundo.
 *
 * El estadio mide `FIELD.MARGIN` (90) por lado, pero reservarle sitio en el
 * encaje es pagar con pantalla por decoracion: en 16:9 el cesped se quedaba en
 * el 65% del area. Y no hace falta, porque el fondo del estadio se dibuja en
 * espacio de PANTALLA (`stadium.layout`) y cubre los bordes igual aunque el
 * mundo desborde: no aparecen franjas negras.
 *
 * 24 deja un filo de estadio enmarcando el campo (a ras del borde se ve roto)
 * y sube el cesped al ~82%.
 */
export const VIEW_MARGIN = 24;
export const FIT_WIDTH = FIELD.WIDTH + VIEW_MARGIN * 2;
export const FIT_HEIGHT = FIELD.HEIGHT + VIEW_MARGIN;

/**
 * Limites de encuadre en coordenadas de MUNDO (el campo empieza en
 * `FIELD.MARGIN`, no en 0). La camara nunca enseña mas alla de esto, asi que
 * al acercarse en los modos grandes no aparece el vacio de detras del estadio.
 *
 * Salen exactamente de los mismos numeros que el encaje (`FIT_WIDTH` y
 * `FIT_HEIGHT`) a proposito: con acercamiento 1 los dos limites de un eje
 * coinciden, la camara queda clavada donde estaba antes y el encuadre de los
 * modos pequeños no se mueve ni un pixel.
 */
export const BOUNDS = {
  minX: FIELD.MARGIN - VIEW_MARGIN,
  maxX: FIELD.MARGIN + FIELD.WIDTH + VIEW_MARGIN,
  minY: FIELD.MARGIN - VIEW_MARGIN / 2,
  maxY: FIELD.MARGIN + FIELD.HEIGHT + VIEW_MARGIN / 2,
} as const;

/**
 * Traduce "quiero este punto del campo en el centro" a la posicion del
 * contenedor de camara, sin salirse de `BOUNDS`.
 *
 * `focusX/focusY` van en coordenadas de CAMPO (0..FIELD.WIDTH), que es como
 * las tiene la simulacion; el desfase de `FIELD.MARGIN` se suma aqui, en un
 * solo sitio.
 */
export function cameraFor(
  view: FrameRect,
  scale: number,
  focusX: number,
  focusY: number,
): { x: number; y: number } {
  return {
    x: axis(view.x, view.width, scale, FIELD.MARGIN + focusX, BOUNDS.minX, BOUNDS.maxX),
    y: axis(view.y, view.height, scale, FIELD.MARGIN + focusY, BOUNDS.minY, BOUNDS.maxY),
  };
}

/**
 * Trozo de CAMPO que se esta viendo, en coordenadas de campo. Lo usa el radar
 * para dibujar donde esta la camara: con los modos acercados el jugador deja
 * de ver el campo entero, y sin esa marca el minimapa no dice en que parte del
 * campo estas mirando.
 */
export function visibleField(
  view: FrameRect,
  scale: number,
  cameraX: number,
  cameraY: number,
): FrameRect {
  return {
    x: (view.x - cameraX) / scale - FIELD.MARGIN,
    y: (view.y - cameraY) / scale - FIELD.MARGIN,
    width: view.width / scale,
    height: view.height / scale,
  };
}

function axis(
  viewStart: number,
  viewSize: number,
  scale: number,
  focusWorld: number,
  minWorld: number,
  maxWorld: number,
): number {
  const centred = viewStart + viewSize / 2 - focusWorld * scale;

  // Si lo encuadrable cabe de sobra en la vista, no hay nada que recortar: se
  // centra. Es el caso de los modos pequeños, donde el limite inferior seria
  // mayor que el superior y un clamp ingenuo daria un salto.
  const lo = viewStart + viewSize - maxWorld * scale;
  const hi = viewStart - minWorld * scale;
  if (lo > hi) return viewStart + viewSize / 2 - ((minWorld + maxWorld) / 2) * scale;

  return Math.min(hi, Math.max(lo, centred));
}
